/**
 * UserInstancesContext - Single shared polling source for /api/compute/instances/user.
 *
 * Why this exists: every call to /api/compute/instances/user triggers a
 * backend<->AWS sync, so N components each running their own interval
 * multiplies AWS API load. Before this context, DashboardSidebar and LabPage
 * each polled independently (duplicate request pairs from one idle tab, plus
 * effect re-arms firing extra immediate fetches). All dashboard consumers now
 * read from this one provider.
 *
 * Polling matrix:
 * - Visible + at least one non-terminal instance (launching/pending/running/
 *   terminating) or an in-flight launch toast -> poll every 30s.
 * - Visible + no active instances -> no interval; refetch on window focus /
 *   tab becoming visible when data is older than STALE_MS.
 * - Hidden -> never fetches (interval ticks are skipped; a fresh fetch happens
 *   on the next visibilitychange->visible if stale).
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { getApiUrl } from '../utils/api';
import { useLaunchNotification } from './LaunchNotificationContext';

export interface UserInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  launch_time: string;
  hourly_cost: number;
  status: string;
  public_ip?: string;
  terminal_ready?: boolean | null;
}

interface UserInstancesContextType {
  /** All instances returned by the backend (any status). */
  instances: UserInstance[];
  /** Instances in a non-terminal state (launching/pending/running/terminating). */
  activeInstances: UserInstance[];
  /** True once at least one fetch has completed successfully. */
  hasLoaded: boolean;
  /** Force an immediate refetch (e.g. after terminating an instance). */
  refresh: () => Promise<void>;
}

const ACTIVE_STATUSES = new Set(['running', 'pending', 'launching', 'terminating']);
/** Poll cadence while there is at least one non-terminal instance. */
const ACTIVE_POLL_MS = 30_000;
/** Data older than this is refetched when the tab regains focus/visibility. */
const STALE_MS = 30_000;
/**
 * Minimum gap between fetches triggered by effect re-arms alone. Guards
 * against a fetch-result flip-flop re-running the driver effect back-to-back
 * (each request costs an AWS sync, so this must never free-run).
 */
const MIN_REFETCH_GAP_MS = 5_000;

const UserInstancesContext = createContext<UserInstancesContextType | null>(null);

export function useUserInstances(): UserInstancesContextType {
  const context = useContext(UserInstancesContext);
  // No-op fallback when used outside the provider (for safety)
  if (!context) {
    return {
      instances: [],
      activeInstances: [],
      hasLoaded: false,
      refresh: async () => {},
    };
  }
  return context;
}

/** Check terminal/Jupyter readiness for a single instance. */
async function checkTerminalReady(instanceId: string, token: string): Promise<boolean> {
  try {
    const response = await fetch(
      `${getApiUrl()}/api/compute/instances/${instanceId}/terminal/available`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.ok) {
      const data = await response.json();
      return data.terminal_ready === true || data.available === true;
    }
  } catch {
    // Not ready yet
  }
  return false;
}

export function UserInstancesProvider({ children }: { children: React.ReactNode }) {
  const [instances, setInstances] = useState<UserInstance[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { launches } = useLaunchNotification();

  // Refs keep the fetch callback stable — per repo react-patterns, polling
  // drivers must not depend on values whose identity changes every render.
  const launchesRef = useRef(launches);
  launchesRef.current = launches;
  const lastFetchedRef = useRef(0);
  const inFlightRef = useRef(false);

  const fetchInstances = useCallback(async () => {
    if (inFlightRef.current) return; // never overlap — each call costs an AWS sync
    const token = localStorage.getItem('authToken');
    if (!token) return;

    inFlightRef.current = true;
    try {
      const response = await fetch(`${getApiUrl()}/api/compute/instances/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;

      const data = await response.json();
      const fetched: UserInstance[] = data.instances || [];

      // Enrich running instances whose terminal readiness is unknown (null).
      // true/false are already persisted by the backend — skip the extra call.
      const enriched = await Promise.all(
        fetched.map(async (inst) => {
          if (inst.status !== 'running' || inst.terminal_ready != null) return inst;
          // The launch-notification poller may already know it's ready
          const launchEntry = launchesRef.current.find(
            (l) => l.instanceId === inst.instance_id
          );
          if (launchEntry?.status === 'ready') {
            return { ...inst, terminal_ready: true };
          }
          // Fallback to terminal check API (triggers backend persistence)
          return { ...inst, terminal_ready: await checkTerminalReady(inst.instance_id, token) };
        })
      );

      lastFetchedRef.current = Date.now();
      setInstances(enriched);
      setHasLoaded(true);
    } catch (error) {
      // Silently fail - consumers treat this as non-critical
      console.debug('Could not fetch user instances:', error);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const activeInstances = useMemo(
    () => instances.filter((i) => ACTIVE_STATUSES.has((i.status || '').toLowerCase())),
    [instances]
  );

  // A launch toast still in progress means an instance may not appear in the
  // list yet — keep polling until it settles. This boolean only flips when a
  // launch starts or finishes, so the driver effect below stays quiet during
  // the toast's own per-instance /status polling.
  const hasActiveLaunch = launches.some((l) => l.status !== 'ready' && l.status !== 'failed');
  const shouldPoll = activeInstances.length > 0 || hasActiveLaunch;

  // Single polling driver. Re-arms only when shouldPoll flips.
  useEffect(() => {
    // Fetch on mount and when polling (re)starts — but only if visible and
    // not fetched within the re-arm guard window.
    if (
      document.visibilityState === 'visible' &&
      Date.now() - lastFetchedRef.current > MIN_REFETCH_GAP_MS
    ) {
      fetchInstances();
    }

    let interval: NodeJS.Timeout | null = null;
    if (shouldPoll) {
      interval = setInterval(() => {
        if (document.visibilityState !== 'visible') return; // pause while hidden
        fetchInstances();
      }, ACTIVE_POLL_MS);
    }

    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchedRef.current > STALE_MS) {
        fetchInstances();
      }
    };
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);

    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
  }, [shouldPoll, fetchInstances]);

  const value = useMemo(
    () => ({ instances, activeInstances, hasLoaded, refresh: fetchInstances }),
    [instances, activeInstances, hasLoaded, fetchInstances]
  );

  return (
    <UserInstancesContext.Provider value={value}>
      {children}
    </UserInstancesContext.Provider>
  );
}
